// ProviderCardField — the ONE file in the portal package that is
// allowed to import a provider's SDK (ADR-005 §Nine non-negotiable
// principles #5). Stripe.js imports live HERE, never in PaymentForm.
//
// Two live providers:
//   - 'mock'   — a fake card form; the Pay button calls props.onSubmit
//                and PaymentForm runs the webhook-simulation flow.
//   - 'stripe' — a real Stripe Elements CardElement. This component
//                confirms the PaymentIntent itself (confirmCardPayment)
//                using the client_secret passed as `client_action_token`,
//                then reports success/error back to PaymentForm. Test vs
//                live is purely the publishable/secret key pair.

import { useMemo, useState } from 'react';
import { loadStripe, type StripeCardElement } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Trans, useTranslation } from 'react-i18next';

import { useAppEnvironment, useStripeConfig } from '../../config/ConfigContext.js';
import { formatCurrency } from '../../i18n/formatters.js';

export type ProviderCardFieldProps = {
  provider_name: string;
  // For Stripe this is the PaymentIntent client_secret; for the mock
  // provider it's the opaque transaction id.
  client_action_token: string;
  amount_cents: number;
  currency: string;
  // Mock provider: the Pay button triggers this (PaymentForm simulates
  // the webhook). Unused by Stripe (it confirms the charge itself).
  onSubmit: () => void;
  disabled?: boolean;
  // SDK providers (Stripe) confirm client-side and report back. The mock
  // ignores these. `provider_transaction_id` is the just-confirmed
  // PaymentIntent id — passed up so PaymentForm can settle it in test lanes
  // (local/stage), where the provider can't deliver a webhook to localhost.
  onProviderSuccess?: (provider_transaction_id?: string) => void;
  onProviderError?: (message: string) => void;
  onSubmittingChange?: (submitting: boolean) => void;
};

// Stripe's recommended test cards, as test-mode PaymentMethod tokens. The
// CardElement is an iframe we can't programmatically type into, so in test
// lanes we confirm the PaymentIntent directly with one of these tokens.
// Ordered by intent: successes first, then declines, then the 3-D Secure
// challenge. See https://stripe.com/docs/testing.
// `labelKey` is the stable slug the render site translates against
// (checkout:card.testTokens.label.*); `label` is only the English
// defaultValue for that lookup, never rendered directly.
const STRIPE_TEST_CARDS: { token: string; labelKey: string; label: string; tone: 'ok' | 'decline' }[] = [
  { token: 'pm_card_visa', labelKey: 'visaApproves', label: 'Visa (approves)', tone: 'ok' },
  { token: 'pm_card_mastercard', labelKey: 'mastercardApproves', label: 'Mastercard (approves)', tone: 'ok' },
  { token: 'pm_card_chargeDeclined', labelKey: 'genericDecline', label: 'Generic decline', tone: 'decline' },
  {
    token: 'pm_card_chargeDeclinedInsufficientFunds',
    labelKey: 'insufficientFunds',
    label: 'Insufficient funds',
    tone: 'decline',
  },
  {
    token: 'pm_card_authenticationRequired',
    labelKey: 'threeDSecureRequired',
    label: '3-D Secure required',
    tone: 'decline',
  },
];

// local/stage-only helper: pay with a test card in one click.
function TestCardPanel({
  disabled,
  onPick,
}: {
  disabled: boolean;
  onPick: (token: string) => void;
}): JSX.Element {
  const { t } = useTranslation(['checkout', 'common']);
  return (
    <div className="banner banner--warn" style={{ marginBottom: 16 }}>
      <Trans i18nKey="card.testTokens.intro" t={t}>
        <strong>Test cards (local/stage only).</strong> The card field can&apos;t be
        auto-filled, so these pay with a test token in one click.
      </Trans>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {STRIPE_TEST_CARDS.map((c) => (
          <button
            key={c.token}
            type="button"
            onClick={() => onPick(c.token)}
            disabled={disabled}
            style={c.tone === 'decline' ? { opacity: 0.85 } : undefined}
          >
            {t(`card.testTokens.label.${c.labelKey}`, c.label)}
          </button>
        ))}
      </div>
    </div>
  );
}

// Currency-safe money formatting — Intl throws a RangeError on an
// unknown/empty currency code, which would otherwise blank the page.
// Routes through the shared locale-aware formatCurrency; falls back to a
// plain string on an invalid currency code so the page never blanks.
function formatMoney(amount_cents: number, currency: string): string {
  const dollars = amount_cents / 100;
  try {
    return formatCurrency(dollars, currency);
  } catch {
    return `${currency || 'USD'} ${dollars.toFixed(2)}`;
  }
}

function MockCardField({
  client_action_token,
  amount_cents,
  currency,
  onSubmit,
  disabled,
}: ProviderCardFieldProps): JSX.Element {
  const { t } = useTranslation(['checkout', 'common']);
  // Mock provider: no real card capture. Visual continuity only; the Pay
  // button triggers the webhook-simulation flow on submit.
  const [number, setNumber] = useState('4242 4242 4242 4242');
  const [exp, setExp] = useState('12/29');
  const [cvc, setCvc] = useState('123');

  return (
    <div>
      <div className="banner banner--warn">
        <Trans i18nKey="card.mock.banner" t={t}>
          <strong>Test mode (mock provider).</strong> No real card is captured. Click
          Pay to simulate a successful charge.
        </Trans>
      </div>
      <div className="form-row">
        <label htmlFor="card-number">{t('card.mock.numberLabel')}</label>
        <input
          id="card-number"
          type="text"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          disabled={disabled}
        />
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <div className="form-row" style={{ flex: 1 }}>
          <label htmlFor="card-exp">{t('card.mock.expiryLabel')}</label>
          <input
            id="card-exp"
            type="text"
            value={exp}
            onChange={(e) => setExp(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="form-row" style={{ flex: 1 }}>
          <label htmlFor="card-cvc">{t('card.mock.cvcLabel')}</label>
          <input
            id="card-cvc"
            type="text"
            value={cvc}
            onChange={(e) => setCvc(e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>
      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: 'pointer' }}>{t('card.mock.showToken')}</summary>
        <pre style={{ fontSize: 11, overflow: 'auto' }}>{client_action_token}</pre>
      </details>
      <button
        type="button"
        className="primary"
        onClick={onSubmit}
        disabled={disabled}
        style={{ marginTop: 16 }}
      >
        {t('card.pay.button', { amount: formatMoney(amount_cents, currency) })}
      </button>
    </div>
  );
}

function StripeCardInner({
  client_action_token,
  amount_cents,
  currency,
  disabled,
  onProviderSuccess,
  onProviderError,
  onSubmittingChange,
}: ProviderCardFieldProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const appEnv = useAppEnvironment();
  const isTestLane = appEnv === 'local' || appEnv === 'stage';
  const { t } = useTranslation(['checkout', 'common']);

  // One confirm path for both the real CardElement and the test-card tokens.
  // `payment_method` is either { card } (typed entry) or a 'pm_card_*' string
  // (Stripe accepts a PaymentMethod id string OR inline { card } data).
  async function confirm(
    payment_method: string | { card: StripeCardElement },
  ): Promise<void> {
    if (!stripe) return;
    setSubmitting(true);
    onSubmittingChange?.(true);
    onProviderError?.('');

    const result = await stripe.confirmCardPayment(client_action_token, {
      payment_method,
    });

    if (result.error) {
      // The provider's own decline message IS customer-facing — safe to show
      // verbatim, and not ours to translate (it's the processor's copy).
      // Our own fallback (when the provider gives no message) IS ours to translate.
      onProviderError?.(result.error.message ?? t('card.error.cardNotCharged'));
      setSubmitting(false);
      onSubmittingChange?.(false);
      return;
    }
    if (result.paymentIntent?.status === 'succeeded') {
      // The payment_intent.succeeded webhook is the source of truth for
      // flipping the quote to 'paid'; Checkout polls until it lands (in test
      // lanes PaymentForm settles it directly since no webhook reaches
      // localhost). Keep the button disabled (submitting stays true).
      onProviderSuccess?.(result.paymentIntent.id);
      return;
    }
    onProviderError?.(t('card.error.paymentIncomplete'));
    setSubmitting(false);
    onSubmittingChange?.(false);
  }

  async function handlePay(): Promise<void> {
    if (!elements) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    await confirm({ card });
  }

  const busy = Boolean(disabled) || submitting;
  return (
    <div>
      {isTestLane && (
        <TestCardPanel disabled={busy || !stripe} onPick={(token) => void confirm(token)} />
      )}
      <div className="form-row">
        <label>{t('card.hostedField.label')}</label>
        <div
          style={{
            padding: '12px 14px',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            background: 'var(--color-surface)',
          }}
        >
          {/* The processor's own hosted-field iframe content — not the
              portal's copy to translate (SAQ-A tokenization boundary). */}
          <CardElement options={{ disabled: busy }} />
        </div>
      </div>
      <button
        type="button"
        className="primary"
        onClick={() => void handlePay()}
        disabled={busy || !stripe}
        style={{ marginTop: 16 }}
      >
        {submitting
          ? t('card.hostedField.processing')
          : t('card.pay.button', { amount: formatMoney(amount_cents, currency) })}
      </button>
    </div>
  );
}

function StripeCardField(props: ProviderCardFieldProps): JSX.Element {
  // The publishable key is RUNTIME config (GET /api/config), not a baked
  // VITE_ var — so one promoted image serves each lane with its own key
  // (pk_test_ on stage, pk_live_ in prod). See config/ConfigContext.tsx.
  // loadStripe is memoized on the key so it runs once, not every render.
  const { publishable_key, loaded } = useStripeConfig();
  const { t } = useTranslation(['checkout', 'common']);
  const stripePromise = useMemo(
    () =>
      publishable_key && publishable_key.startsWith('pk_')
        ? loadStripe(publishable_key)
        : null,
    [publishable_key],
  );

  if (!loaded) {
    return <div className="banner">{t('card.hostedField.loadingForm')}</div>;
  }
  if (!stripePromise) {
    // The wiring detail goes to the console for whoever is debugging the lane;
    // the customer only learns that card payment is unavailable.
    console.error(
      'Card payment is not configured: set PAYMENT_PROVIDER_STRIPE_PUBLISHABLE_KEY (served via GET /api/config), or PAYMENT_PROVIDER=mock locally.',
    );
    return <div className="banner banner--error">{t('card.error.unavailable')}</div>;
  }
  return (
    <Elements stripe={stripePromise}>
      <StripeCardInner {...props} />
    </Elements>
  );
}

export function ProviderCardField(props: ProviderCardFieldProps): JSX.Element {
  const { t } = useTranslation(['checkout', 'common']);
  switch (props.provider_name) {
    case 'mock':
      return <MockCardField {...props} />;
    case 'stripe':
      return <StripeCardField {...props} />;
    default:
      console.error(
        `Payment provider "${props.provider_name}" is not wired in the portal; use PAYMENT_PROVIDER=stripe or mock.`,
      );
      return <div className="banner banner--error">{t('card.error.unavailable')}</div>;
  }
}
